import type { CurrentUser } from "@fscrypto/domain";
import { omit } from "lodash-es";

export function load() {
  if (window.rudderanalytics) {
    const writeKey = window.ENV.RUDDERSTACK_WRITE_KEY;
    const url = window.ENV.RUDDERSTACK_DATAPLANE_URL;
    if (writeKey && url) {
      // configUrl and destSDKBaseURL are overriding the default values, we are sending those requests through cloudflare
      window.rudderanalytics.load(writeKey, url, {
        configUrl: url,
        destSDKBaseURL: `${url}/integrations`,
      });
    }
  }
}

interface CustomTracking {
  [key: string]: string | null | boolean;
}

type TrackingProvider = "rudderstack" | "addressable" | "all";

export type TrackingInterface = "Earn";

export function tracking(
  trackingMsg: string,
  interfaceMsg: TrackingInterface,
  customTrackingObj?: CustomTracking,
  providers: TrackingProvider | TrackingProvider[] = "rudderstack", // Only send to Rudderstack by default
) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  if (window.ENV.APP_ENV === "development") {
    console.log("event tracking", trackingMsg, interfaceMsg, customTrackingObj, providers);
  }

  const providersArray = Array.isArray(providers) ? providers : [providers];
  for (const provider of providersArray) {
    if ((provider === "rudderstack" || provider === "all") && window?.rudderanalytics) {
      const { id, ...rest } = customTrackingObj || {};
      window.rudderanalytics.track(trackingMsg, {
        category: "Website",
        interface: interfaceMsg,
        ...rest,
      });
    }
    if ((provider === "addressable" || provider === "all") && window?.__adrsbl) {
      const { isConversion, ...rest } = customTrackingObj || {};
      const props = Object.entries(rest).map(([name, value]) => ({ name, value }));
      window.__adrsbl.run(trackingMsg, !!isConversion, props);
    }
  }
}

/** Helper to create a track function for a given interface. Just to reduce noise in components. */
export function trackInterface(ifc: TrackingInterface) {
  return (eventName: string, data?: CustomTracking) => {
    tracking(eventName, ifc, data);
  };
}

export function identify(currentUser: CurrentUser) {
  // the id field is reserved in rudderstack so we need to rename it
  // biome-ignore lint/suspicious/noExplicitAny: allow
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const user: any = {
    ...omit(currentUser, "id"),
    ...{ userId: currentUser.id },
  };

  if (window?.rudderanalytics) {
    window.rudderanalytics.identify(
      user.userId,
      {
        email: user.email,
        username: user.username,
        avatar: user.avatarUrl,
        createdAt: user.createdAt,
        userProfileId: user.profileId,
        contextProfileId: user.currentProfileId,
      },
      {
        integrations: {
          "Google Analytics": true,
          "Snowflake Analytics": true,
          Intercom: true,
        },
      },
    );
    if (window.ENV.APP_ENV === "development") {
      console.log("identify tracking", currentUser);
    }
  }
}

export function page(pathName: string) {
  if (window.rudderanalytics) {
    window.rudderanalytics.page(pathName, pathName);
    if (window.ENV.APP_ENV === "development") {
      console.log("page tracking", pathName);
    }
  }
}

const LOGIN_CONVERSION_PATH_KEY = "login-conversion-path";

export function startTrackingLoginConversion(pathname: string) {
  localStorage.setItem(LOGIN_CONVERSION_PATH_KEY, pathname);
}

/**
 * Check if the user is a new user by checking if they were created within the last 5 minutes. If they are, track the login conversion
 * @param currentUser - The current user
 */
export function checkForLoginConversion(currentUser: CurrentUser) {
  const pathname = localStorage.getItem(LOGIN_CONVERSION_PATH_KEY);
  if (pathname) {
    const userCreatedAt = new Date(currentUser.createdAt);
    const fiveMinutesAgo = new Date(new Date().getTime() - 5 * 60 * 1000);
    if (userCreatedAt > fiveMinutesAgo) {
      trackLoginConversion(pathname);
    }
  }
}

function trackLoginConversion(pathname: string) {
  localStorage.removeItem(LOGIN_CONVERSION_PATH_KEY);
  tracking("login_conversion", "Earn", { path: pathname, isConversion: true }, "addressable");
}
